import { FC, ReactNode } from 'react';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';
import { XmarkIcon } from '@shared/assets/icons';
import { useBodyScrollLock } from '@shared/hooks/useBodyScrollLock';
import { colors } from '@shared/tokens/colors.stylex';

export type DrawerPosition = 'left' | 'right';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  position?: DrawerPosition;
  width?: string;
  showCloseButton?: boolean;
  headerContent?: ReactNode;
  drawerPadding?: string;
  headerPadding?: string;
}

export const Drawer: FC<DrawerProps> = ({
  isOpen,
  onClose,
  title,
  children,
  position = 'left',
  width = '580px',
  showCloseButton = true,
  headerContent,
  drawerPadding,
  headerPadding,
}) => {
  useBodyScrollLock(isOpen);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  const hasDefaultHeader = !headerContent && (title || showCloseButton);

  return (
    <>
      <button {...stylex.props(styles.overlay, isOpen && styles.overlayVisible)} onClick={onClose} />

      <div
        {...stylex.props(
          styles.drawer,
          position === 'left' ? styles.drawerLeft : styles.drawerRight,
          isOpen && (position === 'left' ? styles.drawerLeftVisible : styles.drawerRightVisible)
        )}
        style={{ width, ...(drawerPadding && { padding: drawerPadding }) }}
        role="dialog"
        aria-modal="true"
        onKeyDown={handleKeyDown}
      >
        {headerContent}
        {hasDefaultHeader && (
          <div {...stylex.props(styles.header)} style={headerPadding ? { padding: headerPadding } : undefined}>
            {title && <h2 {...stylex.props(styles.title)}>{title}</h2>}
            {showCloseButton && (
              <button {...stylex.props(styles.closeButton)} onClick={onClose} aria-label="Закрыть" type="button">
                <XmarkIcon size="24px" color={colors.primaryIcon} />
              </button>
            )}
          </div>
        )}

        <div {...stylex.props(styles.content)} data-drawer-content>
          {children}
        </div>
      </div>
    </>
  );
};
